/**
 * Auth Routes (Hono)
 *
 * Auto-montaje desde definitions:
 * - All auth actions (login, register, logout, me, etc.) — via authActions
 * - refreshTokenEntity is `expose: false`, so the auto-mount produces an empty
 *   router. We mount it manually here at `/tokens` with create/update disabled
 *   so admins can list/inspect/revoke refresh tokens but not forge them.
 *
 * The `refreshTokens` service is built asynchronously in `auth.init()` and
 * stored on `ctx.services` so this sync factory can retrieve it.
 */

import type { ModuleContext } from '@gzl10/nexus-sdk'
import { refreshTokenEntity } from './auth.entity.js'
import type { HonoApp } from '../../http/types.js'
import { createHonoRouter } from '../../http/types.js'
import { createEntityController } from '../../http/controllers/entity.controller.js'
import { createEntityRouter } from '../../http/routes/entity.routes.js'
import type { EntityService } from '../../http/controllers/types.js'

export function createAuthRoutes(ctx: ModuleContext): HonoApp {
  const app = createHonoRouter()

  // refreshTokenEntity has `expose: false`, so we must build the router manually.
  // The service is async-initialized in auth.init() and registered under
  // 'refreshTokens'. We expose read/list/delete only — create/update happen
  // internally via AuthService.
  const tokensService = ctx.services.get<EntityService>('refreshTokens')
  const tokensController = createEntityController(tokensService, refreshTokenEntity, ctx)

  // Tokens solo se crean/rotan internamente: bloquear create y update.
  delete tokensController.create
  delete tokensController.update

  // Clone the definition with `expose: true` so createEntityRouter mounts routes.
  const exposedDef = { ...refreshTokenEntity, expose: true } as typeof refreshTokenEntity
  const tokensRouter = createEntityRouter(tokensController, exposedDef, ctx, tokensService)

  app.route(refreshTokenEntity.routePrefix ?? '/tokens', tokensRouter)

  return app
}
